"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Star, ShieldCheck, Quote } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
export default function Review() {
  const shouldReduceMotion = useReducedMotion();
  const highlights = ["Substantial roof replacement", "Listed building", "Central London", "Careful protection of exterior", "Professionalism"];
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 18
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="featuredreview" data-nav="light" className="relative overflow-hidden bg-background py-20 lg:py-28">
      {/* Subtle ambient BMF electric blue backdrop glow */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 flex items-center justify-center opacity-60">
        <div className="h-[520px] w-[880px] rounded-full bg-primary/5 blur-3xl" />
      </div>

      <div className="container mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        {/* Header & Structural Eyebrow */}
        <div className="mb-10 text-center md:mb-14">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3.5 py-1 text-xs font-semibold tracking-wide text-primary uppercase">
            <ShieldCheck className="size-3.5" />
            <span>Client verification</span>
          </div>

          <h2 className="mt-4 font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Trusted on demanding projects
          </h2>
        </div>

        {/* Featured Review Showcase Card */}
        <motion.div variants={cardVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }}>
          <Card className="relative overflow-hidden border border-border bg-card p-6 shadow-lg md:p-10 lg:p-12">
            {/* Top decorative structural accent line */}
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary/30 via-primary to-primary/30" />

            <CardContent className="p-0">
              {/* Reviewer Header Row */}
              <div className="flex flex-col gap-4 border-b border-border pb-6 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-default text-xl font-bold tracking-tight text-card-foreground sm:text-2xl">
                      Scott Horn
                    </span>
                    <Badge variant="outline" className="border-primary/25 bg-primary/5 font-medium text-primary">
                      Google Review
                    </Badge>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Verified property owner review
                  </p>
                </div>

                {/* 5 Gold Stars Rating */}
                <div className="flex items-center gap-1" aria-label="5 out of 5 stars rating">
                  {Array.from({
                  length: 5
                }).map((_, starIndex) => <Star key={starIndex} data-index={starIndex} className="size-5 fill-chart-5 text-chart-5" />)}
                </div>
              </div>

              {/* Verbatim Review Quote */}
              <div className="relative py-8 md:py-10">
                <Quote aria-hidden="true" className="pointer-events-none absolute -top-2 -left-2 size-12 text-primary/10 md:-top-4 md:-left-4 md:size-16" />
                <blockquote className="relative z-10 font-default text-base leading-relaxed text-card-foreground sm:text-lg md:text-xl md:leading-relaxed">
                  &ldquo;We engaged BMF to provide the scaffolding for a
                  substantial roof replacement at our property. From the initial
                  consultation through to dismantling, the entire process was
                  handled with an exceptional level of professionalism and
                  efficiency. As the property is a listed building in central
                  London, we were understandably particular about both the
                  standard of workmanship and the care taken with the exterior.
                  Peter was excellent throughout &mdash; knowledgeable,
                  courteous and very clear in explaining the proposed works and
                  timescales. The scaffolding was erected and subsequently
                  removed by his team with remarkable efficiency, whilst taking
                  considerable care to ensure the fabric and exterior of the
                  building were protected throughout. The attention to detail
                  was particularly appreciated. BMF delivered precisely what was
                  promised, when it was promised, and to a very high standard. We
                  would have absolutely no hesitation in recommending them and
                  will certainly use them again.&rdquo;
                </blockquote>
              </div>

              {/* Highlighted Project Points */}
              <div className="border-t border-border pt-6">
                <p className="mb-3 text-xs font-semibold tracking-wider text-muted-foreground uppercase">
                  Key project attributes noted in review
                </p>
                <div className="flex flex-wrap gap-2">
                  {highlights.map((item, index) => <div key={item} data-index={index}>
                      <Badge variant="secondary" className="border border-border/80 bg-muted/60 px-3 py-1 text-xs font-medium text-foreground transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-primary">
                        <span className="mr-1.5 size-1.5 rounded-full bg-primary" />
                        {item}
                      </Badge>
                    </div>)}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>;
}